import { useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table, { Column } from '../../components/Table';
import LookupSelect, { LookupOption } from '../../components/LookupSelect';

const REPORTS = [
  { key: 'daily-attendance', label: 'Daily Attendance', filter: 'date' },
  { key: 'individual-attendance', label: 'Individual Attendance', filter: 'employeeRange' },
  { key: 'exception', label: 'Exception Report', filter: 'exception' },
] as const;

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function TmsReports() {
  const [active, setActive] = useState<(typeof REPORTS)[number]>(REPORTS[0]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [employeeId, setEmployeeId] = useState('');
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [exceptionFlags, setExceptionFlags] = useState({ lateness: true, ot: true, absenteeism: true, missingClocks: true });
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const buildParams = () => {
    if (active.filter === 'date') return { date };
    if (active.filter === 'employeeRange') return { employeeId, fromDate, toDate };
    return { fromDate, toDate, ...exceptionFlags };
  };

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tms/reports/${active.key}`, { params: buildParams() });
      setRows(res.data.rows);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const res = await api.get(`/tms/reports/${active.key}`, { params: { ...buildParams(), format: 'excel' }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.key}.xlsx`;
    a.click();
  };

  const columns: Column<any>[] = rows.length > 0 ? Object.keys(rows[0]).map((k) => ({ key: k, header: k.replace(/_/g, ' ').toUpperCase() })) : [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">TMS Reports</h1>
      <div className="flex gap-2 mb-4 border-b overflow-x-auto">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => { setActive(r); setRows([]); }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${active.key === r.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-end gap-3">
        {active.filter === 'date' && <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
        {active.filter === 'employeeRange' && (
          <>
            <LookupSelect label="Employee" value={employeeId} onChange={setEmployeeId} fetchOptions={fetchEmployeeOptions} placeholder="Search employee..." />
            <Input label="From Date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <Input label="To Date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </>
        )}
        {active.filter === 'exception' && (
          <>
            <Input label="From Date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <Input label="To Date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            {(['lateness', 'ot', 'absenteeism', 'missingClocks'] as const).map((f) => (
              <label key={f} className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={exceptionFlags[f]} onChange={(e) => setExceptionFlags({ ...exceptionFlags, [f]: e.target.checked })} />
                {f}
              </label>
            ))}
          </>
        )}
        <Button onClick={run} loading={loading}>Preview</Button>
        <Button variant="secondary" onClick={exportExcel}>Export to Excel</Button>
      </div>

      <Table columns={columns} rows={rows} loading={loading} emptyMessage="Click Preview to run the report" />
    </div>
  );
}
